//* *
// TODO: Fix Eslint error  */
/* eslint-disable react-compiler/react-compiler */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, {
  createContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
  useCallback,
} from "react";
import axios, { AxiosInstance, AxiosResponse } from "axios";
import { useLocation, useNavigate } from "react-router";
import { datadogLogs } from "@datadog/browser-logs";
import { jwtDecode } from "jwt-decode";
import { logger } from "@platform/browser-logger";
import { saveSession, useUser } from "@platform/user/client";

import * as authApi from "../auth/authApi";
import {
  clearPendingLogin,
  readPendingLogin,
  storePendingLogin,
  type PendingLogin,
  type PendingLoginStep,
} from "../auth/pendingLogin";
import { sessionEndMessage } from "../auth/sessionEnd";
import {
  STATUS_CODE,
  PREV_URL,
  TOKEN_USER_ROLES,
  ROUTE_NAMES,
} from "../constants";
import {
  loginEvent,
  getDisplayName,
  setInternalUserAccount,
  setUserBrokerGroups,
} from "../utils/tracking";
import envVariables from "../projectEnvVariables";
import initializePendo from "../pendo";

import type { GenerateTokenResponse, LoginTokens } from "@platform/api/auth";

interface JWTClaims {
  name?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  emails?: string[];
  [TOKEN_USER_ROLES]?: string[];
  [key: string]: unknown;
}

type MfaState = PendingLoginStep;

type LoginResult = GenerateTokenResponse | { status: number } | undefined;

interface BrokerGroupInfo {
  name: string;
  externalId?: string | null;
  sfExternalId?: string | null;
  [key: string]: unknown;
}

interface AuthContextValue {
  logIn: (
    email: string,
    password: string,
    rememberMe?: boolean,
  ) => Promise<LoginResult>;
  resetPassword: (email: string) => Promise<unknown>;
  logOut: (sessionExpired?: boolean) => Promise<void>;
  validateMfa: (code: string, userId: string) => Promise<void>;
  sendMfaCode: (userId: string) => Promise<void>;
  mfaState: MfaState | null;
  loading: boolean;
  isAuthenticated: boolean;
  token: string | null;
  axiosInstance: AxiosInstance;
  brokerGroupInfo: BrokerGroupInfo | null;
  sessionEndMessage: string | undefined;
}

const API_HOST: string = envVariables.VITE_REACT_APP_API_PATH;

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Interceptors are attached when the instance is created, before any child renders,
// because children's effects run before this provider's and fire requests right away.
const createApiAxios = (
  getAccessToken: () => Promise<string | undefined>,
  endSession: (reason: "unauthorized") => Promise<void>,
): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_HOST,
    headers: { "Content-Type": "application/json" },
  });

  instance.interceptors.request.use(async (config) => {
    const token = await getAccessToken();
    if (!token) {
      return config;
    }

    let payload: unknown = config.data;
    if (payload instanceof FormData) {
      payload = Object.fromEntries(config.data);
    }
    datadogLogs.logger.info(`request [${config.method}]:${config.url}`, {
      payload: capLogPayload(payload),
      url: config.url,
      headers: config.headers,
    });
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      logResponse(response);
      return response;
    },
    (e: { response?: AxiosResponse }) => {
      logResponse(e.response);
      if (e.response?.status === STATUS_CODE.UNAUTHORIZED) {
        void endSession("unauthorized");
      } else {
        throw e;
      }
    },
  );

  return instance;
};

const onHandleAccessToken = (resultAccessToken: string): void => {
  const claims = resultAccessToken
    ? jwtDecode<JWTClaims>(resultAccessToken)
    : null;
  const userType = (claims?.[TOKEN_USER_ROLES] as string[] | undefined)?.[0];
  const userEmail = claims?.email || claims?.emails?.[0];
  if (resultAccessToken) {
    loginEvent(getDisplayName(claims ?? {}), userEmail || "", userType || "");
  }
};

function AuthProvider({ children }: AuthProviderProps): React.JSX.Element {
  const { user, accessToken, getAccessToken, endSession } = useUser();
  const isAuthenticated = user.status === "signedIn";

  const [pendingLogin, setPendingLogin] = useState<PendingLogin | null>(
    readPendingLogin,
  );
  const [brokerGroupInfo, setBrokerGroupInfo] =
    useState<BrokerGroupInfo | null>(null);
  const [loading, setLoading] = useState(false);
  // getAccessToken and endSession are created once per UserProvider, so capturing them is safe.
  const [axiosInstance] = useState(() =>
    createApiAxios(getAccessToken, endSession),
  );
  const location = useLocation();
  const navigate = useNavigate();

  const holdPendingLogin = useCallback((pending: PendingLogin | null) => {
    clearPendingLogin();
    if (pending) {
      storePendingLogin(pending);
    }

    setPendingLogin(pending);
  }, []);

  const completeLogin = useCallback(
    (tokens: LoginTokens, keepSignedIn: boolean) => {
      saveSession(
        {
          accessToken: tokens.access_token,
          idToken: tokens.id_token,
          refreshToken: tokens.refresh_token ?? undefined,
        },
        { keepSignedIn, provider: "password" },
      );
      holdPendingLogin(null);
      onHandleAccessToken(tokens.access_token);

      const prevUrl = (location.state as Record<string, unknown>)?.[
        PREV_URL
      ] as string | undefined;
      if (prevUrl) {
        navigate(prevUrl, { replace: true });
      }
    },
    [holdPendingLogin, location.state, navigate],
  );

  const logIn = useCallback(
    async (
      email: string,
      password: string,
      rememberMe = false,
    ): Promise<LoginResult> => {
      setLoading(true);

      try {
        const result = await authApi.logIn(API_HOST, {
          username: email,
          password,
          keepSignedIn: rememberMe,
        });

        if ("access_token" in result) {
          completeLogin(result, rememberMe);
        }

        if ("enforce" in result || "needsPasswordReset" in result) {
          holdPendingLogin({ step: result, keepSignedIn: rememberMe });
        }

        if ("needsPasswordReset" in result) {
          navigate(`/${ROUTE_NAMES.ONBOARDING_ERROR}?caseId=password-update`);
        }

        return result;
      } catch (error: unknown) {
        logger.error("Amplify login event error", error as Error);

        const status = (error as { status?: unknown } | null)?.status;
        return typeof status === "number" ? { status } : undefined;
      } finally {
        setLoading(false);
      }
    },
    [completeLogin, holdPendingLogin, navigate],
  );

  const resetPassword = useCallback(async (email: string): Promise<unknown> => {
    try {
      return await authApi.resetPassword(API_HOST, email);
    } catch (error: unknown) {
      logger.error("Amplify reset password event error", error as Error);
      return {};
    }
  }, []);

  const logOut = useCallback(
    async (sessionExpired = false): Promise<void> => {
      holdPendingLogin(null);
      await endSession(sessionExpired ? "expired" : "user");
    },
    [endSession, holdPendingLogin],
  );

  const validateMfa = useCallback(
    async (code: string, userId: string): Promise<void> => {
      setLoading(true);

      try {
        const tokens = await authApi.validateMfaCode(API_HOST, code, userId);
        completeLogin(tokens, pendingLogin?.keepSignedIn ?? false);
      } finally {
        setLoading(false);
      }
    },
    [completeLogin, pendingLogin],
  );

  const sendMfaCode = useCallback(async (userId: string): Promise<void> => {
    setLoading(true);

    try {
      await authApi.sendMfaCode(API_HOST, userId);
    } finally {
      setLoading(false);
    }
  }, []);

  const role = user.status === "signedIn" ? user.role : undefined;

  useEffect(() => {
    const findBrokerGroupInfo = async (): Promise<void> => {
      try {
        const prepareUserBrokerResponse = await axiosInstance.post<{
          userBroker?: BrokerGroupInfo;
        }>("brokerportal/v1/prepareUserBroker");
        let userBroker = prepareUserBrokerResponse?.data?.userBroker;
        if (!userBroker) {
          userBroker = {
            name: "N/A",
          };
          setInternalUserAccount(role ?? "");
        }
        setBrokerGroupInfo(userBroker);
        setUserBrokerGroups(userBroker);
      } catch (e: unknown) {
        logger.error("Error fetching broker group info", e as Error);
      }
    };

    if (isAuthenticated) {
      void findBrokerGroupInfo();
    }
  }, [isAuthenticated, role]);

  const signedInUser = user.status === "signedIn" ? user : undefined;
  const userId = signedInUser?.id;
  const email = signedInUser?.email;
  const givenName = signedInUser?.givenName;
  const familyName = signedInUser?.familyName;

  useEffect(() => {
    if (!userId) return;

    initializePendo(userId, email, givenName, familyName);
  }, [userId, email, givenName, familyName]);

  const endMessage = sessionEndMessage(location.state, location.search);

  const value = useMemo(
    () => ({
      logIn,
      resetPassword,
      logOut,
      validateMfa,
      sendMfaCode,
      mfaState: pendingLogin?.step ?? null,
      loading,
      isAuthenticated,
      token: accessToken ?? null,
      axiosInstance,
      brokerGroupInfo,
      sessionEndMessage: endMessage,
    }),
    [
      logIn,
      logOut,
      resetPassword,
      validateMfa,
      sendMfaCode,
      pendingLogin,
      loading,
      isAuthenticated,
      accessToken,
      brokerGroupInfo,
      endMessage,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

const MAX_LOG_PAYLOAD_CHARS = 200000;

function capLogPayload(payload: unknown): unknown {
  if (typeof payload === "string") {
    return payload.length > MAX_LOG_PAYLOAD_CHARS
      ? { truncated: true, sizeChars: payload.length }
      : payload;
  }
  try {
    const serialized = JSON.stringify(payload);
    if (serialized && serialized.length > MAX_LOG_PAYLOAD_CHARS) {
      return {
        truncated: true,
        sizeChars: serialized.length,
      };
    }
  } catch {
    // Avoid emitting oversized payloads when serialization fails (e.g., circular refs).
    return { truncated: true, reason: "non-serializable" };
  }
  return payload;
}

function logResponse(response: AxiosResponse | undefined): void {
  if (!response) return;
  const payload: unknown = capLogPayload(response.data);
  const context = {
    url: response.config.url,
    headers: response.headers,
    payload,
    httpStatus: response.status,
    statusText: response.statusText,
  };
  const message = `response [${response.config.method}]:${response.config.url}`;
  if (response.status >= 500) {
    datadogLogs.logger.error(message, context);
  } else if (response.status >= 400) {
    datadogLogs.logger.warn(message, context);
  } else {
    datadogLogs.logger.info(message, context);
  }
}

export { AuthProvider, AuthContext };
export type { AuthContextValue, MfaState, BrokerGroupInfo };
